/**
 * activityEntryModel.js — ActivityEntryModel typedef + chip/transition helpers.
 *
 * Domain-agnostic. No RMA imports. Normalizers (e.g. toActivityEntry) live in
 * rma/timeline/ and produce these descriptors; ActivityEntryRow consumes them.
 *
 * Model shape — NO `icon` field (icon stays on the ActivityTimeline shell):
 *
 *   ActivityEntryModel = {
 *     id:               string,
 *     timestamp:        string  (ISO-8601 — passed to new Date()),
 *     title:            string  (type label, e.g. "Status Change"),
 *     titleAdornment?:  ReactNode,
 *     chips?:           ActivityChip[],
 *     detail?:          string | ReactNode,
 *     meta?:            string,
 *     body?:            { text: string, expandable?: bool, isQuote?: bool, mono?: bool },
 *     bodyHtml?:        string,
 *                       // SANITIZED email HTML (rma/timeline/sanitizeEmailHtml.js is
 *                       // its ONLY producer). Rendered instead of `body`, and ONLY
 *                       // when the consumer opts in with ActivityEntryRow's
 *                       // `allowHtml`. `body` stays populated as the fail-safe.
 *     quotedBody?:      { label: string, text: string, openLabel?: string },
 *                       // collapsed quoted/forwarded email thread, tucked behind a
 *                       // toggle below the reply. Distinct from secondaryBody so both
 *                       // can co-occur on one email entry.
 *                       // [CORRECTED 2026-08-04] This line used to end "(reply +
 *                       // quote + LLM summary)". That combination no longer exists:
 *                       // the LLM summary is not displayed beside a body any more
 *                       // (plan §E).
 *     quotedBodyHtml?:  string,
 *                       // SANITIZED quoted thread. Same opt-in as bodyHtml; when it
 *                       // is absent the text `quotedBody` is used instead.
 *     secondaryBody?:   { label: string, text: string },
 *                       // A second, collapsed block below the body. TWO meanings
 *                       // today, told apart by the LABEL the normalizer sets — this
 *                       // model stays domain-agnostic and never interprets it:
 *                       //   "Drafted from note" — the smart-compose source note
 *                       //                         (rma/timeline/toActivityEntry.js)
 *                       //   "Sent from mailbox" — N92: the row was read back from the
 *                       //                         mailbox's Sent folder, and `text`
 *                       //                         is the FOLDER name, not a note
 *                       // [CORRECTED 2026-10-01] This block used to say it means
 *                       // "Drafted from note" only; before that it carried an LLM
 *                       // summary. Neither holds today. A THIRD meaning needs this
 *                       // list updated by hand — nothing checks it.
 *     attachments?:     Array<{ id, name, mimeType, sizeBytes, href, isImage,
 *                               unavailableReason }>,
 *                       // Message attachment footer. Model flag only — no prop.
 *     footer?:          ReactNode,  // interactive CTA / extra content rendered below the body, above meta/actions
 *     actions?:         ReactNode,
 *   }
 *
 *   ActivityChip (union):
 *     { kind:'chip', label, color?, variant?, mono?, href?, tooltip? }
 *     { kind:'transition', from:{label,color?}, to:{label,color?}, tooltip? }
 */

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * chip(label, opts?) → ActivityChip descriptor of kind:'chip'.
 *
 * @param {string} label
 * @param {{color?:string, variant?:string, mono?:boolean, href?:string, tooltip?:string}} [opts]
 * @returns {{ kind: 'chip', label: string, color?: string, variant?: string, mono?: boolean, href?: string, tooltip?: string }}
 */
export function chip(label, opts = {}) {
  return { kind: 'chip', label, ...opts };
}

/**
 * transition(from, to, opts?) → ActivityChip descriptor of kind:'transition'.
 *
 * `from` and `to` are `{ label, color? }` — the normalizer pre-resolves human
 * labels + STATUS_CONFIG colors so this common helper stays dumb (no rma/constants import).
 *
 * @param {{ label: string, color?: string }} from
 * @param {{ label: string, color?: string }} to
 * @param {{ tooltip?: string }} [opts]
 * @returns {{ kind: 'transition', from: {label:string,color?:string}, to: {label:string,color?:string}, tooltip?: string }}
 */
export function transition(from, to, opts = {}) {
  return { kind: 'transition', from, to, ...opts };
}
